import { type Dispatch } from "redux";
import dataService from "../../../../Application/services/dataServices/typedDataService";
import { beginAsyncOperation } from "../../../../Application/slices/asyncOperationSlice";
import { PackSettingsEditSuccess } from "../../../../Application/services/realTimeNotification/events/library/libraryEvents";
import {
  fetchBegin,
  fetchSuccess,
  fetchFailure,
  updateBegin,
  updateSuccess,
  updateFailure,
} from "../slices/packSettingsSlice";
import { updatePackBegin } from "../actionTypes/packEntityStateActionType";
import { updatePackCommandSuccess, packUpdated } from "../actions/packEntityStateActions";
import type { PackSettingsView, PackSettingsPayload, PackPricingSettingsView } from "../../types/state";
import { createAction } from "@reduxjs/toolkit";

export const fetchPackSettings = (packId: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(fetchBegin());

    try {
      const response = await dataService.packs.getPackSettingsAsync(packId);
      const mappedSettings = mapToView(response.data);
      dispatch(fetchSuccess(mappedSettings));
      return mappedSettings;
    } catch (error) {
      dispatch(fetchFailure(error as Error));
      throw error;
    }
  };
};

export const updatePackSettings = (payload: PackSettingsPayload) => {
  const { id, ...settings } = payload;
  const updateEntityStateBegin = createAction(updatePackBegin);

  return async (dispatch: Dispatch) => {
    dispatch(updateBegin());
    dispatch(updateEntityStateBegin());
    dispatch(beginAsyncOperation({ id: id!, action: PackSettingsEditSuccess }));

    try {
      await dataService.packs.updatePackSettingsAsync(id!, settings);
      dispatch(updateSuccess(mapToView(settings)));
      dispatch(updatePackCommandSuccess({ id: id! }));
      dispatch(packUpdated({ id: id! }));
      return settings;
    } catch (error) {
      dispatch(updateFailure(error as Error));
      throw error;
    }
  };
};

const mapToView = (settingsDto: PackSettingsPayload): PackSettingsView => {
  const settings: PackSettingsView = {
    isFree: settingsDto.isFree,
    pricingTypeId: settingsDto.pricingTypeId,
    pricingSettings: {
      billingTermId: null,
      pricePerUser: undefined,
      isTrialAllowed: false,
      trialPeriodId: null,
    },
    licensingSettings: {
      unlimitedLicenses: true,
      noExpiration: true,
    },
  };

  if (hasPricingSettings(settingsDto)) {
    const pricingSource = settingsDto.pricingSettings;
    if (pricingSource) {
      settings.pricingSettings = mapLicensingSettings(settingsDto.pricingSettings!);
    }
  }

  if (settingsDto.licensingSettings) {
    settings.licensingSettings = {
      unlimitedLicenses: settingsDto.licensingSettings.unlimitedLicenses,
      noExpiration: settingsDto.licensingSettings.noExpiration,
    };
  }

  function hasPricingSettings(settingsDto: PackSettingsPayload): boolean {
    return settingsDto.pricingSettings !== undefined;
  }

  function mapLicensingSettings(pricingSource: PackPricingSettingsView): PackPricingSettingsView {
    return {
      pricePerUser: pricingSource.pricePerUser!.toFixed(2) as unknown as number,
      billingTermId: !pricingSource.billingTermId ? null : pricingSource.billingTermId,
      isTrialAllowed: pricingSource.isTrialAllowed,
      trialPeriodId: pricingSource.trialPeriodId ?? null,
    };
  }

  return settings;
};
